import { z } from 'zod'
import { ReviewApprovalCitationSchema, ReviewCitationInputSchema } from './citations'

export const SuggestedActionStatusSchema = z.object({
  type: z.literal('completed'),
  timestamp: z.iso.datetime(),
  userId: z.uuid(),
  commentId: z.uuid().optional(),
})
export type SuggestedActionStatus = z.infer<typeof SuggestedActionStatusSchema>

export const AddRequestCommentActionSchema = z.object({
  id: z.uuid(),
  type: z.literal('ADD_REQUEST_COMMENT'),
  payload: z.object({
    text: z.string(),
  }),
  status: SuggestedActionStatusSchema.optional(),
})
export const AmendmentRequestActionSchema = z.object({
  id: z.uuid(),
  type: z.literal('AMENDMENT_REQUEST'),
  payload: z.object({
    message: z.string(),
    questionName: z.string(),
    questionTitle: z.string(),
    requestFormId: z.uuid(),
  }),
  status: SuggestedActionStatusSchema.optional(),
})
export const SuggestedActionSchema = z.discriminatedUnion('type', [AddRequestCommentActionSchema, AmendmentRequestActionSchema])
type _SuggestedAction = z.infer<typeof SuggestedActionSchema>
export type SuggestedAction<ActionType extends _SuggestedAction['type'] = _SuggestedAction['type']> = Extract<
  _SuggestedAction,
  { type: ActionType }
>

export const ParagraphInputSchema = z.object({
  paragraph: z
    .string()
    .describe(
      'One paragraph covering a single point or claim — write 1–4 sentences. May start with a `## ` heading if the content calls for it. Do not put multiple separate points in one paragraph. Do not repeat a `## ` heading that has already been used in a previous paragraph — each section heading must appear at most once across all paragraphs.'
    ),
  sources: z.array(ReviewCitationInputSchema).describe('Citations directly supporting this paragraph'),
})
export type ParagraphInput = z.infer<typeof ParagraphInputSchema>

export const ResolvedParagraphSchema = z.object({
  paragraph: z.string(),
  sources: z.array(ReviewApprovalCitationSchema),
})
export type ResolvedParagraph = z.infer<typeof ResolvedParagraphSchema>

// `unavailable`: a required precedent input couldn't be loaded (the anchor summary hadn't landed), distinct
// from `none_found` (no similar reviews exist). Absent on the output when the precedents slice was inactive.
export const PrecedentsStatusSchema = z.enum(['available', 'none_found', 'unavailable'])
export type PrecedentsStatus = z.infer<typeof PrecedentsStatusSchema>

// `unavailable`: the anchor request could not be read, distinct from `none_found` (nothing similar enough).
export type SimilarRequestsStatus = 'available' | 'none_found' | 'unavailable'

export const AgentDecisionSchema = z.enum(['RECOMMEND_APPROVAL', 'RECOMMEND_REJECTION', 'NEEDS_MORE_INFO'])
export type AgentDecision = z.infer<typeof AgentDecisionSchema>

export const AgentConfidenceSchema = z.enum(['HIGH', 'MEDIUM', 'LOW'])
export type AgentConfidence = z.infer<typeof AgentConfidenceSchema>

export const AgentDecisionOutputSchema = z.object({
  // Stable id of the agent_runs row — the feedback `outputId`, assigned at persist (not by the LLM). Optional
  // for runs and step data persisted before it existed.
  runId: z.string().optional(),
  decision: AgentDecisionSchema,
  confidence: AgentConfidenceSchema,
  reasoning: z.array(ResolvedParagraphSchema),
  suggestedActions: z.array(SuggestedActionSchema),
  // Not LLM-generated — merged from slice retrieval at persist. Optional/absent when the slice was inactive.
  precedentsStatus: PrecedentsStatusSchema.optional(),
})
export type AgentDecisionOutput = z.infer<typeof AgentDecisionOutputSchema>

export type AgentDecisionRaw = Omit<AgentDecisionOutput, 'reasoning' | 'runId'> & {
  reasoning: ParagraphInput[]
}

export const AgentStepStatusSchema = z.enum(['PENDING', 'FAILED', 'COMPLETED'])
export type AgentStepStatus = z.infer<typeof AgentStepStatusSchema>

export type StepAgentsAggregate = { status: 'PENDING' } | { status: 'FAILED' } | { status: 'COMPLETED'; completedAt?: string }

// A step's runs are created in one statement and share created_at, so there is no "latest run" to read the
// step's status from: it is derived from the set, one run per agent. Completed once every agent has, at the
// instant the last one did.
export const aggregateStepAgentsState = (
  runs: { status: 'PENDING' | 'COMPLETED' | 'FAILED'; completedAt: string | null }[]
): StepAgentsAggregate | undefined => {
  if (runs.length === 0) return undefined
  if (runs.some((run) => run.status === 'PENDING')) return { status: 'PENDING' }
  if (runs.some((run) => run.status === 'FAILED')) return { status: 'FAILED' }
  const completedAt = runs
    .flatMap((run) => (run.completedAt ? [run.completedAt] : []))
    .reduce<string | undefined>(
      (latest, instant) => (latest === undefined || Date.parse(instant) > Date.parse(latest) ? instant : latest),
      undefined
    )
  return { status: 'COMPLETED', ...(completedAt && { completedAt }) }
}

export type AgentStepData = {
  attemptCount: number
  status: AgentStepStatus
  errorMessage?: string
  completedAt?: string
}

// Post-run handoff trigger from the task-agent orchestrator, which owns whether to escalate (it
// skips paused requests and steps whose tasks are all complete). A stage opts in to the task agent
// by handling this in its processUpdate — like TASK_AGENT_EXECUTION_FINISHED — and only has to send
// its block's assignees-added notification, built from the step.
export type EscalateToHumanAssigneesUpdate = {
  action: 'ESCALATE_TO_HUMAN_ASSIGNEES'
}

export type AutonomousMode = 'APPROVE' | 'REJECT' | 'NEEDS_MORE_INFO'
